<script lang="ts">
    // Config
    import { BUILD } from '~/config/build';

    // Shared Components
    import MainSection from '@amp/web-app-components/src/components/structure/MainSection.svelte';
    import CommerceModalContainer from '@amp/web-app-components/src/components/Commerce/ModalContainer.svelte';
    import WarningModal from '@amp/web-app-components/src/components/Modal/WarningModal.svelte';
    import AwayFromHomeBanner from '@amp/web-app-components/src/components/banners/AwayFromHomeBanner.svelte';

    // Types
    import type { Page } from '~/jet/models';

    // Components
    import Content from '~/components/structure/Content.svelte';
    import Fonts from '~/components/structure/Fonts.svelte';
    import PageResolver from '~/components/PageResolver.svelte';
    import PlayerBar from '~/components/structure/Players/PlayerBar.svelte';
    import Header from '~/components/structure/Header.svelte';
    import Footer from '~/components/structure/Footer.svelte';
    import UpsellBanner from '~/components/UpsellBanner.svelte';
    import CreatePlaylistModal from '~/components/playlists/CreatePlaylistModal.svelte';
    import EditPlaylistModal from '~/components/playlists/EditPlaylistModal.svelte';
    import StyleContext from '~/components/utils/StyleContext/StyleContext.svelte';

    // Utils
    import { getLocale } from '~/utils/locale';
    import {
        regions,
        storefrontNameTranslations,
    } from '~/utils/storefront-data';
    import { defaultRoute } from '~/constants/navigation-items';
    import { CommerceRoute, getCommerce } from '~/utils/commerce';

    // Stores
    import { getI18n } from '~/stores/i18n';

    const { state: commerceState } = getCommerce();
    const locale = getLocale();
    const i18n = getI18n();

    $: storefront = locale.storefront;
    $: language = locale.language;
    $: user = {
        isSignedIn: $commerceState?.user !== null,
        userStorefront: $commerceState?.user?.subscriptionStorefront,
    };

    export let page: Promise<Page> = new Promise(() => {});
    export let isFirstPage: boolean = true;
</script>

<svelte:head>
    <meta name="version" content={BUILD} />
</svelte:head>

<Fonts {language} />

<StyleContext let:scrollIsLocked let:isOpenDrawer>
    <div
        class="app-container"
        class:scroll-is-locked={scrollIsLocked}
        class:is-drawer-open={isOpenDrawer}
        data-testid="app-container"
    >
        <Header />
        <PlayerBar />
        <MainSection>
            <Content>
                <PageResolver {page} {isFirstPage} />
            </Content>
            <Footer {page} />
        </MainSection>
        <CommerceModalContainer />
        <UpsellBanner />
        <WarningModal />
        <AwayFromHomeBanner
            translateFn={$i18n.t}
            {locale}
            {user}
            {regions}
            {storefrontNameTranslations}
            {defaultRoute}
        />
        {#if typeof window !== 'undefined'}
            <CreatePlaylistModal />
            <EditPlaylistModal />
        {/if}
    </div>
</StyleContext>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/types' as *;
    @use 'ac-sasskit/core/math' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/viewports' as *;

    .app-container {
        background-color: initial;
        display: grid;

        /* Fallback to vh if dvh not supported */
        height: 100vh;
        height: 100dvh;

        /* Header, page content (grows to fill available space), footer */
        grid-template-areas:
            'structure-header'
            'structure-upsell'
            'structure-main-section'
            'structure-locale-switcher';
        grid-template-columns: minmax(0, 1fr);
        grid-gap: 0;
        grid-template-rows: $global-header-mobile-contracted-height auto 1fr auto;

        @media (--sidebar-visible) {
            --toast-position-inline-start: calc(
                50% + #{math-decimal-round(
                        unit-set($global-sidebar-ratio-to-page, vw),
                        2
                    )} / 2
            );
            grid-template-areas:
                'structure-header structure-main-section'
                'structure-upsell structure-upsell'
                'structure-locale-switcher structure-locale-switcher';
            grid-template-columns:
                math-decimal-round(
                    unit-set($global-sidebar-ratio-to-page, vw),
                    2
                )
                minmax(0, 1fr);
            grid-template-rows: minmax(0, 1fr) auto auto;
        }

        @media (--sidebar-large-visible) {
            --toast-position-inline-start: calc(
                50% + #{$global-sidebar-width-large} / 2
            );
            grid-template-columns: $global-sidebar-width-large minmax(0, 1fr);
        }

        &.is-android {
            // Dynamic height fix for Android
            width: 100%;
            height: 100%;
            position: fixed;
            top: 0;
        }

        @media (--target-touchscreen) {
            // Prevent mobile browser UI controls from overlapping content
            max-height: stretch; // autoprefixes to `-webkit-fill-available`
        }
    }
</style>
